import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { Banner } from "@/components/auth/FormShell";
import { useAuth } from "@/lib/AuthContext";
import { api, ApiError } from "@/lib/api";
import styles from "./AdminWaitlist.module.css";

interface WaitlistEntry {
  username: string;
  sub: string;
  email: string | null;
  workspaceName: string | null;
  createdAt: string | null;
}

interface ListResponse {
  users: WaitlistEntry[];
}

/**
 * Admin console for approving waitlisted users. Lists every CONFIRMED
 * but disabled user (i.e. people who finished email verification and
 * got dropped into the waitlist by the PostConfirmation Lambda), with
 * Approve / Reject buttons per row.
 *
 * Gated on the caller's `sm-admins` Cognito group membership both
 * client-side (this component) and server-side (the API handlers
 * return 403 to non-admins).
 */
export function AdminWaitlist() {
  const auth = useAuth();
  const [users, setUsers] = useState<WaitlistEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function refresh() {
    setError(null);
    try {
      const resp = await api<ListResponse>("/admin/waitlist");
      setUsers(resp.users);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  useEffect(() => {
    if (auth.status === "authenticated" && auth.user?.isAdmin) {
      void refresh();
    }
  }, [auth.status, auth.user?.isAdmin]);

  if (auth.status === "loading") {
    return (
      <main className={styles.main}>
        <p className={styles.loading}>Loading…</p>
      </main>
    );
  }
  if (auth.status === "anonymous") {
    return <Navigate to="/signin?next=/admin/waitlist" replace />;
  }
  if (!auth.user?.isAdmin) {
    return (
      <main className={styles.main}>
        <div className={styles.inner}>
          <h1 className={styles.title}>Not authorised</h1>
          <p className={styles.body}>
            This page is for platform admins. If you should have access, ask another admin
            to add you to the <code>sm-admins</code> Cognito group.
          </p>
          <Btn href="/account" variant="secondary">
            Back to your account
          </Btn>
        </div>
      </main>
    );
  }

  async function act(username: string, action: "approve" | "reject") {
    setBusy(`${action}:${username}`);
    setError(null);
    try {
      await api(`/admin/waitlist/${action}`, {
        method: "POST",
        body: { username },
      });
      // Drop the row optimistically; refresh re-queries Cognito truth.
      setUsers((cur) => (cur ?? []).filter((u) => u.username !== username));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <main className={styles.main}>
      <div className={styles.inner}>
        <header className={styles.header}>
          <div>
            <p className={styles.kicker}>Admin</p>
            <h1 className={styles.title}>Waitlist</h1>
            <p className={styles.body}>
              Verified email, disabled account. Approving flips the Cognito
              <code> Enabled</code> flag — the user can sign in immediately afterward
              with the password they set during sign-up. Rejecting deletes the user;
              their pending workspace stays in DynamoDB and needs the runbook cleanup.
            </p>
          </div>
          <Btn variant="secondary" onClick={() => void refresh()} iconLeft={<Icon name="refresh" size={12} />}>
            Refresh
          </Btn>
        </header>

        {error && (
          <Banner variant="danger" icon={<Icon name="info" size={14} />}>
            {error}
          </Banner>
        )}

        {users === null && !error && <p className={styles.loading}>Loading waitlist…</p>}

        {users && users.length === 0 && (
          <div className={styles.empty}>
            <Icon name="check" size={28} color="var(--success)" />
            <h2 className={styles.emptyTitle}>Inbox zero.</h2>
            <p className={styles.body}>
              Nobody is waiting for approval. New sign-ups land here automatically.
            </p>
          </div>
        )}

        {users && users.length > 0 && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Email</th>
                <th>Workspace</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.username}>
                  <td className={styles.email}>{u.email ?? "—"}</td>
                  <td>{u.workspaceName ?? "—"}</td>
                  <td className={styles.when}>{formatWhen(u.createdAt)}</td>
                  <td className={styles.actionsCell}>
                    <Btn
                      size="sm"
                      variant="primary"
                      onClick={() => void act(u.username, "approve")}
                      loading={busy === `approve:${u.username}`}
                      disabled={busy !== null}
                    >
                      Approve
                    </Btn>
                    <Btn
                      size="sm"
                      variant="ghost"
                      onClick={() => void act(u.username, "reject")}
                      loading={busy === `reject:${u.username}`}
                      disabled={busy !== null}
                    >
                      Reject
                    </Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </main>
  );
}

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
